/**
 * Environment configuration builder
 */

import type { EnvironmentConfig, EnvironmentName } from './types';
import { ENV_KEYS } from './types';
import { LOCAL_API_URL } from './apiTargets';

/**
 * Build configuration for local mock environment
 */
export function buildLocalConfig(): EnvironmentConfig {
  const mockEmail = import.meta.env[ENV_KEYS.MOCK_USER_EMAIL] || 'dev@example.com';
  const mockName = import.meta.env[ENV_KEYS.MOCK_USER_NAME] || 'Local Developer';

  return {
    name: 'local',
    displayName: 'Local (Mock)',
    cognitoAuthority: '',
    cognitoClientId: 'mock',
    cognitoDomain: '',
    redirectUri: window.location.origin + '/',
    postLogoutRedirectUri: window.location.origin + '/',
    scope: 'openid email profile',
    apiUrl: LOCAL_API_URL,
    appUri: window.location.origin,
    isMock: true,
  };
}

/**
 * Build configuration for development environment
 */
export function buildDevConfig(): EnvironmentConfig {
  const authority = import.meta.env[ENV_KEYS.DEV_AUTHORITY];
  const clientId = import.meta.env[ENV_KEYS.DEV_CLIENT_ID];
  const domain = import.meta.env[ENV_KEYS.DEV_DOMAIN];
  const apiUrl = import.meta.env[ENV_KEYS.DEV_API_URL];

  if (!authority || !clientId) {
    throw new Error(
      `Development environment requires VITE_DEV_COGNITO_AUTHORITY and VITE_DEV_COGNITO_CLIENT_ID. ` +
      `Please add them to your .env file.`
    );
  }

  if (!apiUrl) {
    throw new Error(
      `Development environment requires VITE_DEV_API_URL. ` +
      `Please add it to your .env file. See README "Using as a Template" section.`
    );
  }

  return {
    name: 'dev',
    displayName: 'Development',
    cognitoAuthority: authority,
    cognitoClientId: clientId,
    cognitoDomain: domain || '',
    redirectUri: window.location.origin + '/',
    postLogoutRedirectUri: window.location.origin + '/',
    scope: 'openid email profile aws.cognito.signin.user.admin',
    apiUrl,
    appUri: window.location.origin,
    isMock: false,
  };
}

/**
 * Build configuration for production environment
 */
export function buildProdConfig(): EnvironmentConfig {
  const authority = import.meta.env[ENV_KEYS.PROD_AUTHORITY];
  const clientId = import.meta.env[ENV_KEYS.PROD_CLIENT_ID];
  const domain = import.meta.env[ENV_KEYS.PROD_DOMAIN];
  const apiUrl = import.meta.env[ENV_KEYS.PROD_API_URL];

  if (!authority || !clientId) {
    throw new Error(
      `Production environment requires VITE_PROD_COGNITO_AUTHORITY and VITE_PROD_COGNITO_CLIENT_ID. ` +
      `Please add them to your .env file.`
    );
  }

  if (!apiUrl) {
    throw new Error(
      `Production environment requires VITE_PROD_API_URL. ` +
      `Please add it to your .env file. See README "Using as a Template" section.`
    );
  }

  return {
    name: 'prod',
    displayName: 'Production',
    cognitoAuthority: authority,
    cognitoClientId: clientId,
    cognitoDomain: domain || '',
    redirectUri: window.location.origin + '/',
    postLogoutRedirectUri: window.location.origin + '/',
    scope: 'openid email profile aws.cognito.signin.user.admin',
    apiUrl,
    appUri: window.location.origin,
    isMock: false,
  };
}

/**
 * Ensure URL uses HTTPS for deployed environments.
 * Handles cases where terraform injects HTTP URLs.
 */
function ensureHttps(url: string | undefined): string | undefined {
  if (!url) return url;
  // Replace http:// with https:// for non-localhost URLs
  if (url.startsWith('http://') && !url.includes('localhost')) {
    return url.replace('http://', 'https://');
  }
  return url;
}

/**
 * Build configuration for deployed environment (terraform-injected vars)
 */
export function buildDeployedConfig(): EnvironmentConfig {
  const authority = import.meta.env[ENV_KEYS.AUTHORITY];
  const clientId = import.meta.env[ENV_KEYS.OIDC_CLIENT_ID];
  const redirectUri = import.meta.env[ENV_KEYS.OIDC_REDIRECT_URI];
  const postLogoutRedirectUri = import.meta.env[ENV_KEYS.OIDC_POST_LOGOUT_REDIRECT_URI];
  const scope = import.meta.env[ENV_KEYS.OIDC_SCOPE];
  const rawApiUrl = import.meta.env[ENV_KEYS.API_URL];
  const apiUrl = ensureHttps(rawApiUrl);
  const cognitoDomain = import.meta.env[ENV_KEYS.COGNITO_DOMAIN];

  if (!authority || !clientId) {
    throw new Error(
      `Deployed environment requires VITE_AUTHORITY and VITE_OIDC_CLIENT_ID. ` +
      `These should be injected by terraform.`
    );
  }

  // Use explicitly injected VITE_ENVIRONMENT from CI/CD
  const envName: EnvironmentName = (import.meta.env[ENV_KEYS.ENVIRONMENT] as EnvironmentName) || 'dev';

  return {
    name: envName,
    displayName: envName === 'dev' ? 'Development (Deployed)' : 'Production (Deployed)',
    cognitoAuthority: authority,
    cognitoClientId: clientId,
    cognitoDomain: cognitoDomain || '',
    redirectUri: redirectUri || window.location.origin + '/',
    postLogoutRedirectUri: postLogoutRedirectUri || window.location.origin + '/',
    scope: scope || 'openid email profile aws.cognito.signin.user.admin',
    apiUrl: apiUrl || window.location.origin,
    appUri: window.location.origin,
    isMock: false,
  };
}

/**
 * Load environment configuration based on selected environment
 */
export function loadEnvironmentConfig(): EnvironmentConfig {
  // Check if deployed environment (terraform-injected variables)
  if (import.meta.env[ENV_KEYS.AUTHORITY]) {
    console.log('Detected deployed environment (terraform-injected configuration)');
    return buildDeployedConfig();
  }

  // Local development - determine from VITE_ENVIRONMENT
  const envName = (import.meta.env[ENV_KEYS.ENVIRONMENT] as EnvironmentName) || 'local';
  console.log(`Loading configuration for environment: ${envName}`);

  switch (envName) {
    case 'local':
      return buildLocalConfig();
    case 'dev':
      return buildDevConfig();
    case 'prod':
      return buildProdConfig();
    default:
      console.warn(`Unknown environment "${envName}", defaulting to local mock`);
      return buildLocalConfig();
  }
}
